import React, { useEffect, useState } from "react";
import { Accordion, Badge, Button, Col, Form, InputGroup, Row, Table } from "react-bootstrap";
import IsoDatePicker from "../../../components/IsoDatePicker";
import { formatDateDMY } from "../../../utils/dateFormat";
import {
  deleteBookingCustomerPayment,
  editBookingCustomerPayment,
  getBookingCustomerPayments,
  recordBookingCustomerPayment,
} from "../../../services/bookingCustomerPaymentApi";
import {
  CUSTOMER_PAYMENT_METHODS,
  DeletedPayments,
  EditedMark,
  PaymentRowActions,
  usePaymentCorrections,
} from "./PaymentCorrections";

const blankPayment = () => ({
  amount: "",
  paymentDate: new Date().toISOString().slice(0, 10),
  paymentMethod: "UPI",
  referenceNumber: "",
  note: "",
});

export default function CustomerPayments({ bookingId, currentTotalPrice, onTotalPaidChange }) {
  const [summary, setSummary] = useState(null);
  const [payment, setPayment] = useState(blankPayment);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!bookingId) {
      setSummary(null);
      return;
    }
    setLoading(true);
    setError("");
    getBookingCustomerPayments(bookingId)
      .then(setSummary)
      .catch((err) => setError(err?.response?.data?.message || "Could not load customer payments."))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const recordPayment = async () => {
    setSaving(true);
    setError("");
    try {
      const next = await recordBookingCustomerPayment(bookingId, {
        ...payment,
        amount: Number(payment.amount || 0),
      });
      setSummary(next);
      setPayment(blankPayment());
      onTotalPaidChange?.(Number(next.totalPaid || 0));
    } catch (err) {
      setError(err?.response?.data?.message || "Could not record customer payment.");
    } finally {
      setSaving(false);
    }
  };

  const corrections = usePaymentCorrections({
    methods: CUSTOMER_PAYMENT_METHODS,
    editPayment: (item, change) => editBookingCustomerPayment(bookingId, item.id, change),
    deletePayment: (item, { reason }) => deleteBookingCustomerPayment(bookingId, item.id, reason),
    onChanged: (next) => {
      setSummary(next);
      onTotalPaidChange?.(Number(next?.totalPaid || 0));
    },
  });

  const displayTotalPrice = currentTotalPrice != null && Number(currentTotalPrice) > 0
    ? Number(currentTotalPrice)
    : Number(summary?.totalPrice || 0);
  const displayTotalPaid = Number(summary?.totalPaid || 0);
  const displayBalance = Math.max(0, displayTotalPrice - displayTotalPaid);
  // More received than the booking total: a payment entered twice, or a refund owed. The balance
  // alone shows 0 here, so nobody noticed (14 bookings on 22/10/2026).
  const overReceived = displayTotalPrice > 0 ? Math.round((displayTotalPaid - displayTotalPrice) * 100) / 100 : 0;

  return (
    <Accordion>
      <Accordion.Item eventKey="customer-payments">
        <Accordion.Header>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <span className="fw-semibold">Customer Payments Received</span>
            {summary && (
              <>
                <Badge bg="secondary">Total INR {displayTotalPrice.toLocaleString()}</Badge>
                <Badge bg="success">Received INR {displayTotalPaid.toLocaleString()}</Badge>
                <Badge bg={displayBalance > 0 ? "warning" : "success"} text={displayBalance > 0 ? "dark" : undefined}>
                  Balance INR {displayBalance.toLocaleString()}
                </Badge>
                {overReceived > 0 && <Badge bg="danger">Received INR {overReceived.toLocaleString()} more than total</Badge>}
              </>
            )}
          </div>
        </Accordion.Header>
        <Accordion.Body>
          {loading && <div className="small text-muted">Loading customer payments...</div>}
          {error && <div className="small text-danger mb-2">{error}</div>}
          {!bookingId && <div className="small text-muted">Save the booking before recording customer payments.</div>}
          {bookingId && !loading && (
            <>
              {overReceived > 0 && (
                <div className="border border-danger rounded p-2 mb-3 small text-danger">
                  Payments received are INR {overReceived.toLocaleString()} more than the booking total. Check for a payment
                  entered twice, or whether a refund is owed to the customer.
                </div>
              )}
              {Number(summary?.openingAdvance || 0) > 0 && (
                <div className="small text-muted mb-3">
                  Opening advance from existing booking: ₹{Number(summary.openingAdvance).toLocaleString()}
                </div>
              )}
              <div className="fw-semibold mb-2">Record Customer Payment</div>
              <Row className="g-2 mb-3">
                <Col md={3}><Form.Label>Amount *</Form.Label><InputGroup><InputGroup.Text>₹</InputGroup.Text><Form.Control type="number" min="0.01" step="0.01" value={payment.amount} onChange={(e) => setPayment((old) => ({ ...old, amount: e.target.value }))} /></InputGroup></Col>
                <Col md={3}><Form.Label>Payment Date *</Form.Label><IsoDatePicker value={payment.paymentDate} onChange={(value) => setPayment((old) => ({ ...old, paymentDate: value }))} /></Col>
                <Col md={3}><Form.Label>Method *</Form.Label><Form.Select value={payment.paymentMethod} onChange={(e) => setPayment((old) => ({ ...old, paymentMethod: e.target.value }))}><option value="UPI">UPI</option><option value="BANK_TRANSFER">Bank Transfer</option><option value="CASH">Cash</option><option value="CARD">Card</option><option value="OTHER">Other</option></Form.Select></Col>
                <Col md={3}><Form.Label>Reference / UTR</Form.Label><Form.Control value={payment.referenceNumber} onChange={(e) => setPayment((old) => ({ ...old, referenceNumber: e.target.value }))} /></Col>
                <Col md={9}><Form.Label>Payment Note *</Form.Label><Form.Control value={payment.note} onChange={(e) => setPayment((old) => ({ ...old, note: e.target.value }))} placeholder="Payment purpose or installment details" /></Col>
                <Col md={3} className="d-flex align-items-end"><Button className="w-100" disabled={saving || !Number(payment.amount) || !payment.note.trim()} onClick={recordPayment}>Record Payment</Button></Col>
              </Row>
              <div className="table-responsive">
                <Table bordered size="sm" className="mb-0">
                  <thead><tr><th style={{ width: 44 }}>#</th><th>Date</th><th>Amount</th><th>Method</th><th>Reference</th><th>Note</th><th style={{ width: 84 }}>Actions</th></tr></thead>
                  <tbody>
                    {(summary?.transactions || []).map((item, index) => <tr key={item.id}><td className="small fw-semibold">{index + 1}</td><td>{formatDateDMY(item.paymentDate)}</td><td>₹{Number(item.amount || 0).toLocaleString()}<EditedMark payment={item} /></td><td>{item.paymentMethod?.replaceAll("_", " ")}</td><td>{item.referenceNumber || "-"}</td><td>{item.note}</td><td><PaymentRowActions payment={item} corrections={corrections} disabled={saving} /></td></tr>)}
                    {!summary?.transactions?.length && <tr><td colSpan={7} className="text-center text-muted">No customer payment transactions recorded.</td></tr>}
                  </tbody>
                </Table>
              </div>
              <DeletedPayments payments={summary?.deletedTransactions} />
              {corrections.modal}
            </>
          )}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
}
